import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { usePasswordResetLink } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
/**
 * **The reset screen's link step** (`USR-004` AC-2, AC-8).
 *
 * The platform mails this link when somebody asks to reset a password on an account whose address
 * was never verified — Cognito's own recovery refuses to mail that account a code. Following it
 * confirms the address and sets the new password in one step, and **the screen does not say so**:
 * it reads like any password reset (Sumit, 2026-09-10 — the person *"doesn't need to know if the
 * account is verified or not"*). Confirming is not a new grant: holding the mailed link is the
 * same proof the verification link accepts.
 *
 * It is a step of the existing recovery screens rather than a screen of its own: the member app's
 * `/app/recovery` and the organizer console's `/recovery` render it when the URL carries
 * `?token=`, and the code flow (`PasswordRecoveryForm`) otherwise.
 *
 * **A used or expired link explains itself and offers no way through.** The link is inspected
 * before any input renders; the two dead states have no password field at all, only the sentence
 * and a way back to asking for a new one. Unknown and used read the same — a guessed token must
 * learn nothing about which accounts exist.
 */
export function PasswordResetLinkForm({ token, onSignIn, onRequestAnother, describePasswordViolation, classNames, errorMessages, onScreenStateChange, }) {
    const classes = mergeFormClasses(classNames);
    const { inspect, complete } = usePasswordResetLink();
    const [inspection, setInspection] = React.useState(null);
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [validationError, setValidationError] = React.useState(null);
    const [done, setDone] = React.useState(null);
    const runInspect = inspect.run;
    const reinspect = React.useCallback(async () => {
        if (token.length === 0) {
            setInspection({ state: 'used', emailAddress: null });
            return;
        }
        try {
            setInspection(await runInspect({ token }));
        }
        catch {
            // A link the server could not even read is, to its holder, a link that does not work.
            setInspection({ state: 'used', emailAddress: null });
        }
    }, [runInspect, token]);
    React.useEffect(() => {
        void reinspect();
    }, [reinspect]);
    // Reported from an effect rather than during render: the host's handler sets state, and a
    // parent setState during a child's render is the loop React warns about.
    const screenState = done !== null ? 'done' : inspection === null ? 'checking' : inspection.state === 'usable' ? 'ready' : 'dead';
    React.useEffect(() => {
        onScreenStateChange?.(screenState);
    }, [onScreenStateChange, screenState]);
    const handleSubmit = async (event) => {
        event.preventDefault();
        const violation = describePasswordViolation?.(newPassword) ??
            (newPassword.length === 0 ? 'Choose a new password.' : null);
        if (violation !== null) {
            setValidationError(violation);
            return;
        }
        if (newPassword !== confirmPassword) {
            setValidationError('Passwords do not match.');
            return;
        }
        setValidationError(null);
        try {
            setDone(await complete.run({ token, newPassword }));
        }
        catch {
            // The error is on the hook. The link may have died between inspection and submit — spent
            // in another tab, or the hour ran out — so look again: a dead link renders as dead.
            await reinspect();
        }
    };
    if (done !== null) {
        return (_jsxs("div", { className: classes.form, children: [_jsx("p", { className: classes.success, role: "status", children: "Your new password is set. You have been signed out everywhere else \u2014 sign in with the new password to continue." }), _jsx("button", { type: "button", className: classes.button, onClick: onSignIn, children: "Sign in" })] }));
    }
    if (inspection === null) {
        return _jsx("p", { className: classes.hint, children: "Checking your link\u2026" });
    }
    if (inspection.state !== 'usable') {
        const completionError = describeError(complete.error, errorMessages);
        return (_jsxs("div", { className: classes.form, children: [_jsx("p", { className: classes.error, role: "alert", children: inspection.state === 'expired'
                        ? 'This link has expired — reset links last one hour. Ask for a new one and use it within the hour.'
                        : 'This link has already been used or is not valid. Each link works once; ask for a new one if you still need to reset your password.' }), completionError !== null ? _jsx("p", { className: classes.hint, children: completionError }) : null, _jsx("button", { type: "button", className: classes.button, onClick: onRequestAnother, children: "Ask for a new link" })] }));
    }
    const error = describeError(complete.error, errorMessages);
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-reset-link-new-password", children: "New password" }), _jsx("input", { id: "auth-ui-reset-link-new-password", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: newPassword, onChange: (event) => setNewPassword(event.target.value) })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-reset-link-confirm-password", children: "Confirm new password" }), _jsx("input", { id: "auth-ui-reset-link-confirm-password", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: confirmPassword, onChange: (event) => setConfirmPassword(event.target.value) })] }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null, error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: complete.loading, children: complete.loading ? 'Saving…' : 'Set new password' })] }));
}
//# sourceMappingURL=PasswordResetLinkForm.js.map